import { getEnv } from '@monogrid/env'
import { useModernizr, useModernizrStyleClasses, usePlatform } from '@monogrid/vue-boost'
import type { i18nInjectionKey } from '@monogrid/vue-boost/i18n'
import type { MetaFlatInput } from '@unhead/schema'
import { useHead, useSeoMeta } from '@unhead/vue'
import type { MaybeRefOrGetter } from '@vueuse/core'
import { toValue } from '@vueuse/core'
import { inject, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

import { globalInjectionKeys } from '@/main'

// resolve path based on USE_CMS variable
export { getSeoImageUrl } from 'USEHEAD_UTILS'

export type HeadOptions = {
  siteName?: MaybeRefOrGetter<string>,
  siteDescription?: MaybeRefOrGetter<string>,
  defaultOgTitle?: MaybeRefOrGetter<string>,
  defaultOgDescription?: MaybeRefOrGetter<string>,
  defaultImage?: MaybeRefOrGetter<string>,
}

export type UseSeoMetaInput = {
  title?: MaybeRefOrGetter<string>
} & {
  [key in keyof MetaFlatInput]: MaybeRefOrGetter<MetaFlatInput[key]>
}

let defaults: HeadOptions

export function useSeo (data: UseSeoMetaInput) {
  const entry = useSeoMeta({
    twitterCard: 'summary_large_image',
    ...data,
    description: () => toValue(data.description) || toValue(defaults.siteDescription),
    ogDescription: () => toValue(data.ogDescription) || toValue(data.description) || toValue(defaults.defaultOgDescription) || toValue(defaults.siteDescription),
    ogTitle: () => toValue(data.ogTitle) || toValue(data.title) || toValue(defaults.defaultOgTitle) || toValue(defaults.siteName),
    ogImage: () => toValue(data.ogImage) || toValue(defaults.defaultImage)
  })

  onUnmounted(() => entry && entry.dispose())
}

export function setupHead (options: HeadOptions) {
  const env = getEnv()
  // https://vue-i18n.intlify.dev/guide/advanced/composition.html#basic-usage
  const i18n = globalInjectionKeys.i18n
    ? inject(globalInjectionKeys.i18n as typeof i18nInjectionKey)()
    : undefined
  const route = useRoute()
  const testClasses = useModernizrStyleClasses({ ...useModernizr(), ...usePlatform() })

  defaults = options

  const head = useHead({
    htmlAttrs: {
      class: testClasses,
      dir: () => i18n?.dir.value || 'ltr',
      lang: () => i18n?.locale.value || 'en'
    },
    meta: [
      { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
      { property: 'og:type', content: 'website' },
      { property: 'og:image:type', content: 'image/jpeg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '675' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'mobile-web-app-capable', content: 'yes' },
      { name: 'description', content: options.siteDescription },
      { property: 'og:title', content: () => toValue(options.defaultOgTitle) || toValue(options.siteName) },
      { property: 'og:description', content: () => toValue(options.defaultOgDescription) || toValue(options.siteDescription) },
      { property: 'og:image', content: options.defaultImage },
      { property: 'og:url', content: () => `https://${env.SERVICE_TARGET_DOMAIN}${route.fullPath}` }
    ],
    link: [
      { rel: 'canonical', href: () => `https://${env.SERVICE_TARGET_DOMAIN}` }
    ],
    titleTemplate: (title?: string) => !title ? toValue(options.siteName) : `${title} | ${toValue(options.siteName)}`
  })

  onUnmounted(() => head && head.dispose())
}
